/**
 * First-party cookie consent (LA-159 / GDPR).
 *
 * Drives Google Consent Mode v2 so the GTM + GA4 tags loaded in index.html do
 * not store cookies or track until the visitor consents. Used by the
 * CookieConsent banner and the footer "Cookie settings" link.
 *
 * When a Cookiebot Domain Group ID (VITE_COOKIEBOT_CBID) is configured,
 * Cookiebot takes over consent management and this first-party banner stays
 * hidden — see `cookiebotEnabled`.
 */
import { reactive } from 'vue';

const STORAGE_KEY = 'lm_cookie_consent';
// Bump when the categories change so returning visitors are re-asked.
const CONSENT_VERSION = 1;
// Re-ask after this many days even without changes (typical GDPR practice).
const CONSENT_MAX_AGE_DAYS = 180;

export const CATEGORIES = ['preferences', 'statistics', 'marketing'];

export const cookiebotEnabled = !!import.meta.env.VITE_COOKIEBOT_CBID;

// Shared reactive state so the banner and the footer link stay in sync.
export const consentState = reactive({
    open: false,
    decided: false,
    choices: {
        necessary: true, // always on, cannot be disabled
        preferences: false,
        statistics: false,
        marketing: false,
    },
});

function gtag() {
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
}

function readStored() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (data.version !== CONSENT_VERSION) return null;
        const ageDays = (Date.now() - (data.timestamp || 0)) / 86400000;
        if (ageDays > CONSENT_MAX_AGE_DAYS) return null;
        return data;
    } catch (e) {
        return null;
    }
}

function persist(choices) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            version: CONSENT_VERSION,
            timestamp: Date.now(),
            choices,
        }));
    } catch (e) {
        /* storage unavailable (private mode) — consent applies for this session only */
    }
}

/**
 * Translate our category choices into Google Consent Mode v2 signals and push
 * the update so already-loaded Google tags react immediately.
 */
function applyConsent(choices) {
    const granted = (on) => (on ? 'granted' : 'denied');
    gtag('consent', 'update', {
        analytics_storage: granted(choices.statistics),
        ad_storage: granted(choices.marketing),
        ad_user_data: granted(choices.marketing),
        ad_personalization: granted(choices.marketing),
        functionality_storage: granted(choices.preferences),
        personalization_storage: granted(choices.preferences),
        security_storage: 'granted',
    });
    // Custom event so GTM can fire non-Google tags on consent categories too.
    window.dataLayer.push({ event: 'cookie_consent_update', consent: { ...choices } });
}

function save(choices) {
    const full = { necessary: true, ...choices };
    consentState.choices = full;
    consentState.decided = true;
    consentState.open = false;
    persist(full);
    applyConsent(full);
}

export function acceptAll() {
    save({ preferences: true, statistics: true, marketing: true });
}

export function rejectAll() {
    save({ preferences: false, statistics: false, marketing: false });
}

export function saveChoices(choices) {
    save({
        preferences: !!choices.preferences,
        statistics: !!choices.statistics,
        marketing: !!choices.marketing,
    });
}

/** Re-open the banner so a visitor can change their consent at any time. */
export function openSettings() {
    if (cookiebotEnabled) {
        if (window.Cookiebot && typeof window.Cookiebot.renew === 'function') {
            window.Cookiebot.renew();
        }
        return;
    }
    consentState.open = true;
}

/**
 * Initialise on app start: re-apply a stored choice (so tags are un-gated for
 * returning visitors) or open the banner for a first-time visitor. No-op when
 * Cookiebot is managing consent.
 */
export function initConsent() {
    if (cookiebotEnabled) return;

    const stored = readStored();
    if (stored) {
        consentState.choices = stored.choices;
        consentState.decided = true;
        applyConsent(stored.choices);
    } else {
        consentState.open = true;
    }
}
